import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, TrendingUp, PieChart as PieChartIcon, Activity, SlidersHorizontal, Loader2 } from 'lucide-react';
import ChartContainer from './visualizations/ChartContainer';
import duckdbService from '../services/duckdbService';

const chartIcons = {
  bar: BarChart3,
  line: TrendingUp,
  pie: PieChartIcon,
  scatter: Activity,
};

const quoteIdentifier = (value) => `"${String(value).replace(/"/g, '""')}"`;

const getColumnType = (stats) => {
  const dbType = String(stats?.dbType || '').toLowerCase();
  if (dbType.includes('date') || dbType.includes('time')) return 'date';
  if (stats?.type === 'numeric') return 'numeric';
  if (stats?.type === 'categorical') return 'categorical';
  if (stats?.uniqueCount !== undefined && stats.uniqueCount < 20) return 'categorical';
  if (dbType.includes('int') || dbType.includes('double') || dbType.includes('decimal') || dbType.includes('numeric') || dbType.includes('real')) {
    return 'numeric';
  }
  return 'text';
};

const buildSuggestions = (profile) => {
  if (!profile?.columnStats || !profile?.columns?.length) return [];

  const numericCols = [];
  const categoricalCols = [];
  const dateCols = [];

  profile.columns.forEach((column) => {
    const stats = profile.columnStats[column];
    const type = getColumnType(stats);
    if (type === 'numeric') {
      numericCols.push(column);
    } else if (type === 'date') {
      dateCols.push(column);
    } else if (type === 'categorical') {
      categoricalCols.push(column);
    }
  });

  const suggestions = [];

  if (dateCols.length > 0 && numericCols.length > 0) {
    suggestions.push({
      id: `trend-${dateCols[0]}-${numericCols[0]}`,
      title: `${numericCols[0]} over time`,
      description: 'Track the trend of a numeric metric.',
      chartType: 'line',
      xAxis: dateCols[0],
      yAxis: numericCols[0],
      agg: 'sum'
    });
  }

  if (categoricalCols.length > 0 && numericCols.length > 0) {
    suggestions.push({
      id: `bar-${categoricalCols[0]}-${numericCols[0]}`,
      title: `${numericCols[0]} by ${categoricalCols[0]}`,
      description: 'Compare values across categories.',
      chartType: 'bar',
      xAxis: categoricalCols[0],
      yAxis: numericCols[0],
      agg: 'sum'
    });

    suggestions.push({
      id: `pie-${categoricalCols[0]}-${numericCols[0]}`,
      title: `Share of ${numericCols[0]} by ${categoricalCols[0]}`,
      description: 'View proportions for each category.',
      chartType: 'pie',
      xAxis: categoricalCols[0],
      yAxis: numericCols[0],
      agg: 'sum'
    });
  }

  if (categoricalCols.length > 0) {
    suggestions.push({
      id: `count-${categoricalCols[0]}`,
      title: `Count by ${categoricalCols[0]}`,
      description: 'See the distribution of categories.',
      chartType: 'bar',
      xAxis: categoricalCols[0],
      yAxis: null,
      agg: 'count'
    });
  }

  if (numericCols.length > 1) {
    suggestions.push({
      id: `scatter-${numericCols[0]}-${numericCols[1]}`,
      title: `${numericCols[0]} vs ${numericCols[1]}`,
      description: 'Compare two numeric measures.',
      chartType: 'scatter',
      xAxis: numericCols[0],
      yAxis: numericCols[1],
      agg: 'none'
    });
  }

  return suggestions.slice(0, 4);
};

const buildQueryConfig = (tableName, config) => {
  const chartType = config.chartType;
  if (chartType === 'scatter') {
    const xAxis = quoteIdentifier(config.xAxis);
    const yAxis = quoteIdentifier(config.yAxis);
    return {
      sql: `SELECT TRY_CAST(${xAxis} AS DOUBLE) AS x, TRY_CAST(${yAxis} AS DOUBLE) AS y FROM ${tableName} WHERE ${xAxis} IS NOT NULL AND ${yAxis} IS NOT NULL LIMIT 1000`,
      chartConfig: { xAxis: 'x', yAxis: 'y' },
      chartType
    };
  }

  if (chartType === 'table') {
    return {
      sql: `SELECT * FROM ${tableName} LIMIT 50`,
      chartConfig: {},
      chartType: 'table'
    };
  }

  const xAxis = quoteIdentifier(config.xAxis);
  const agg = config.agg || 'count';
  const hasYAxis = config.yAxis && agg !== 'count';
  const valueExpr = hasYAxis
    ? `${agg.toUpperCase()}(TRY_CAST(${quoteIdentifier(config.yAxis)} AS DOUBLE))`
    : 'COUNT(*)';

  const sql = `
    SELECT ${xAxis} AS category, ${valueExpr} AS value
    FROM ${tableName}
    WHERE ${xAxis} IS NOT NULL
    GROUP BY ${xAxis}
    ORDER BY ${chartType === 'line' ? 'category' : 'value'} ${chartType === 'line' ? 'ASC' : 'DESC'}
    LIMIT 30
  `;

  if (chartType === 'pie') {
    return {
      sql,
      chartConfig: { nameKey: 'category', valueKey: 'value' },
      chartType
    };
  }

  return {
    sql,
    chartConfig: { xAxis: 'category', yAxis: 'value' },
    chartType
  };
};

const ChartSuggestions = ({ profile, selectedFile }) => {
  const [activeConfig, setActiveConfig] = useState(null);
  const [draftConfig, setDraftConfig] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  const suggestions = useMemo(() => buildSuggestions(profile), [profile]);

  const columns = useMemo(() => profile?.columns || [], [profile]);
  const numericColumns = useMemo(() => (
    columns.filter((column) => getColumnType(profile?.columnStats?.[column]) === 'numeric')
  ), [columns, profile]);

  useEffect(() => {
    if (!suggestions.length) {
      setActiveConfig(null);
      setData([]);
      return;
    }
    if (!activeConfig || !suggestions.find((item) => item.id === activeConfig.id)) {
      setActiveConfig(suggestions[0]);
      setDraftConfig(suggestions[0]);
      setEditorOpen(false);
    }
  }, [suggestions, activeConfig]);

  useEffect(() => {
    const runQuery = async () => {
      if (!activeConfig || !selectedFile || !profile?.tableName) return;
      setLoading(true);
      try {
        const queryConfig = buildQueryConfig(profile.tableName, activeConfig);
        const result = await duckdbService.executeQuery(queryConfig.sql, selectedFile.file_id);
        setData(result.result || []);
      } catch (error) {
        console.error('Chart suggestion query failed:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    runQuery();
  }, [activeConfig, selectedFile, profile]);

  const handleSelectSuggestion = (suggestion) => {
    setActiveConfig(suggestion);
    setDraftConfig(suggestion);
    setEditorOpen(false);
  };

  const handleOpenEditor = () => {
    if (!activeConfig) return;
    setDraftConfig(activeConfig);
    setEditorOpen(true);
  };

  const handleApplyEditor = () => {
    if (!draftConfig) return;
    setActiveConfig(draftConfig);
    setEditorOpen(false);
  };

  const updateDraft = (updates) => {
    setDraftConfig((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...updates };
      if (['scatter', 'table'].includes(next.chartType)) {
        next.agg = 'none';
      }
      if (['bar', 'line', 'pie'].includes(next.chartType) && !['count', 'sum', 'avg'].includes(next.agg)) {
        next.agg = 'count';
      }
      if (!next.xAxis && columns.length > 0) {
        next.xAxis = columns[0];
      }
      if (next.chartType === 'scatter' && numericColumns.length > 1) {
        next.yAxis = next.yAxis || numericColumns[1];
      }
      if (['bar', 'line', 'pie'].includes(next.chartType) && numericColumns.length > 0) {
        next.yAxis = next.agg === 'count' ? null : (next.yAxis || numericColumns[0]);
      }
      return next;
    });
  };

  if (!selectedFile || !profile) return null;

  return (
    <div className="bg-white border border-[rgba(32,30,29,0.12)] shadow-brand-sm rounded-brand-md p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-ink">Chart Suggestions</h3>
          <p className="text-sm text-sand-600">Auto-generated charts based on your schema.</p>
        </div>
        <button
          type="button"
          onClick={handleOpenEditor}
          disabled={!activeConfig}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-[rgba(32,30,29,0.18)] text-ink hover:bg-sand-100 text-xs transition-colors disabled:opacity-50"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Customize
        </button>
      </div>

      {suggestions.length === 0 ? (
        <div className="text-sm text-sand-600">Not enough typed columns to suggest charts yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {suggestions.map((suggestion) => {
            const Icon = chartIcons[suggestion.chartType] || BarChart3;
            const isActive = activeConfig?.id === suggestion.id;
            return (
              <button
                key={suggestion.id}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className={`text-left rounded-brand-sm border px-3 py-2 transition-colors ${
                  isActive ? 'border-bill-500 bg-bill-100' : 'border-[rgba(32,30,29,0.12)] bg-white hover:bg-sand-100'
                }`}
              >
                <div className="flex items-center gap-2 text-sm text-ink">
                  <Icon className="w-4 h-4 text-sand-600" />
                  {suggestion.title}
                </div>
                <div className="text-xs text-sand-600 mt-1">{suggestion.description}</div>
              </button>
            );
          })}
        </div>
      )}

      {editorOpen && draftConfig && (
        <div className="border border-[rgba(32,30,29,0.12)] bg-sand-100 rounded-brand-sm p-3 space-y-3">
          <div className="text-sm font-medium text-ink">Simple Chart Editor</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <label className="text-sand-700">
              Chart type
              <select
                value={draftConfig.chartType}
                onChange={(event) => updateDraft({ chartType: event.target.value })}
                className="mt-1 w-full bg-white border border-[rgba(32,30,29,0.18)] rounded-lg px-2 py-1.5 text-ink"
              >
                <option value="bar">Bar</option>
                <option value="line">Line</option>
                <option value="pie">Pie</option>
                <option value="scatter">Scatter</option>
                <option value="table">Table</option>
              </select>
            </label>
            {draftConfig.chartType !== 'table' && (
              <label className="text-sand-700">
                X Axis
                <select
                  value={draftConfig.xAxis || ''}
                  onChange={(event) => updateDraft({ xAxis: event.target.value })}
                  className="mt-1 w-full bg-white border border-[rgba(32,30,29,0.18)] rounded-lg px-2 py-1.5 text-ink"
                >
                  {columns.map((column) => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </label>
            )}
            {['bar', 'line', 'pie', 'scatter'].includes(draftConfig.chartType) && (
              <label className="text-sand-700">
                Y Axis
                <select
                  value={draftConfig.yAxis || ''}
                  onChange={(event) => updateDraft({ yAxis: event.target.value })}
                  className="mt-1 w-full bg-white border border-[rgba(32,30,29,0.18)] rounded-lg px-2 py-1.5 text-ink"
                >
                  {numericColumns.map((column) => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </label>
            )}
            {['bar', 'line', 'pie'].includes(draftConfig.chartType) && (
              <label className="text-sand-700">
                Aggregation
                <select
                  value={draftConfig.agg || 'count'}
                  onChange={(event) => updateDraft({ agg: event.target.value })}
                  className="mt-1 w-full bg-white border border-[rgba(32,30,29,0.18)] rounded-lg px-2 py-1.5 text-ink"
                >
                  <option value="count">Count</option>
                  <option value="sum">Sum</option>
                  <option value="avg">Average</option>
                </select>
              </label>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditorOpen(false)}
              className="px-3 py-1.5 rounded-lg text-xs text-sand-600 hover:text-ink transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyEditor}
              className="px-3 py-1.5 rounded-lg text-xs bg-bill-500 text-white hover:bg-bill-600 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {activeConfig && (
        <div className="bg-white border border-[rgba(32,30,29,0.12)] rounded-brand-sm p-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-sand-600 py-8">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading chart data...
            </div>
          ) : (
            <ChartContainer
              visualization={{
                name: activeConfig.title,
                chart_type: buildQueryConfig(profile.tableName, activeConfig).chartType,
                chart_config: buildQueryConfig(profile.tableName, activeConfig).chartConfig,
                description: activeConfig.description
              }}
              data={data}
              isEditable={false}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ChartSuggestions;
